import fetch from '../../utils/fetch';
import { normalizeAboutUs } from '../../utils/aboutUs';
import { resolveMapFeatureState } from '../../utils/mapFeature';
import { cks } from '../../utils/tool';

const ABOUT_US_STORAGE_KEY = 'heycharge_about_us'

const MACHINE_PRECHECK_ERROR_META = {
  STATION_NOT_FOUND: { code: 50101, message: 'Station not found' },
  STATION_NOT_SET_UP: { code: 50102, message: 'Station not set up' },
  STATION_OFFLINE: { code: 50106, message: 'Station offline' },
  NO_POWER_BANK_AVAILABLE: { code: 50110, message: 'No power banks available' },
}

const normalizeBooleanFlag = (value) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true'
  return false
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key)

function readCachedAboutUs() {
  if (typeof window === 'undefined' || !window.localStorage) return {}
  try {
    return normalizeAboutUs(JSON.parse(window.localStorage.getItem(ABOUT_US_STORAGE_KEY) || '{}'))
  } catch (_) {
    return {}
  }
}

function cacheAboutUs(aboutUs) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(ABOUT_US_STORAGE_KEY, JSON.stringify(normalizeAboutUs(aboutUs)))
  } catch (_) {}
}

function normalizeLoginMethods(loginMethod, fallbackAutoQuickLogin) {
  const normalizedLoginMethod = Array.isArray(loginMethod)
    ? loginMethod.map(method => {
      if (!method || typeof method !== 'object') return method
      if (method.name !== 'quick_login') return method

      return {
        ...method,
        auto_quick_login: normalizeBooleanFlag(method.auto_quick_login),
      }
    })
    : loginMethod

  const quickLoginMethod = Array.isArray(normalizedLoginMethod)
    ? normalizedLoginMethod.find(method => method && method.name === 'quick_login')
    : undefined

  return {
    login_method: normalizedLoginMethod,
    auto_quick_login: normalizeBooleanFlag(
      quickLoginMethod?.auto_quick_login ?? fallbackAutoQuickLogin
    ),
  }
}

function resolveMachinePrecheckError(station = {}, deviceCode) {
  if (!station || typeof station !== 'object' || Array.isArray(station)) return undefined
  if (!Object.keys(station).length) return undefined

  if (hasOwn(station, 'exists') && !normalizeBooleanFlag(station.exists)) {
    return {
      ...MACHINE_PRECHECK_ERROR_META.STATION_NOT_FOUND,
      device_code: deviceCode,
    }
  }

  if (Number(station.online_status) !== 1) {
    return {
      ...MACHINE_PRECHECK_ERROR_META.STATION_OFFLINE,
      device_code: deviceCode,
    }
  }

  if (!station.outlets_id) {
    return {
      ...MACHINE_PRECHECK_ERROR_META.STATION_NOT_SET_UP,
      device_code: deviceCode,
    }
  }

  if (!normalizeBooleanFlag(station.has_available_powerbank)) {
    return {
      ...MACHINE_PRECHECK_ERROR_META.NO_POWER_BANK_AVAILABLE,
      device_code: deviceCode,
    }
  }

  return undefined
}

function normalizeAppInitConfig(data = {}) {
  const brand = data.brand || {};
  const webSettings = data.web_settings || {};
  const policyTerms = data.policy_terms || {};
  const incomingCaptcha = data.captcha || {};
  const incomingRecaptcha = data.recaptcha || {};
  const incomingTurnstile = data.turnstile || {};
  const mapFeatureState = resolveMapFeatureState();

  const turnstile = {
    ...incomingTurnstile,
    turnstile_open: normalizeBooleanFlag(incomingTurnstile.turnstile_open ?? incomingTurnstile.turnstileOpen),
    turnstile_site_key: incomingTurnstile.turnstile_site_key || incomingTurnstile.turnstile_sitekey || '',
  }

  const recaptcha = {
    ...incomingRecaptcha,
    recaptcha_open: normalizeBooleanFlag(incomingRecaptcha.recaptcha_open),
    recaptcha_site_key: incomingRecaptcha.recaptcha_site_key || '',
  }

  let captcha
  if (Object.keys(incomingCaptcha).length > 0) {
    captcha = {
      ...incomingCaptcha,
      enabled: normalizeBooleanFlag(incomingCaptcha.enabled),
      provider: incomingCaptcha.provider,
      site_key: incomingCaptcha.site_key || '',
    }
  } else if (turnstile.turnstile_open) {
    captcha = {
      enabled: true,
      provider: 'turnstile',
      site_key: turnstile.turnstile_site_key,
    }
  } else if (recaptcha.recaptcha_open) {
    captcha = {
      enabled: true,
      provider: 'recaptcha',
      site_key: recaptcha.recaptcha_site_key,
    }
  } else {
    captcha = {
      enabled: false,
      provider: undefined,
      site_key: '',
    }
  }

  const { login_method, auto_quick_login } = normalizeLoginMethods(
    data.login_method,
    data.auto_quick_login ?? webSettings.auto_quick_login
  )

  return {
    ...data,
    brand_name: data.brand_name ?? brand.name,
    brand_logo: data.brand_logo ?? brand.logo,
    brand_favicon: data.brand_favicon ?? brand.icon,
    country_code: data.country_code ?? webSettings.country_code ?? '',
    default_web_locale: data.default_web_locale ?? webSettings.default_web_locale,
    web_lang_options: data.web_lang_options ?? webSettings.lang_options,
    recharge_amount_gradient: data.recharge_amount_gradient ?? webSettings.recharge_amount_gradient ?? [],
    default_currency: data.default_currency ?? webSettings.default_currency,
    privacy_policy: data.privacy_policy ?? policyTerms.privacy_policy ?? '#',
    terms_and_conditions: data.terms_and_conditions ?? policyTerms.terms_and_conditions ?? '#',
    market_link: data.market_link || {},
    about_us: normalizeAboutUs(data.about_us),
    login_method,
    auto_quick_login,
    is_guest: normalizeBooleanFlag(data.is_guest),
    user_info_collection: Array.isArray(data.user_info_collection) ? data.user_info_collection : [],
    venue_info: data.venue_info || {},
    station: data.station || {},
    captcha,
    recaptcha,
    turnstile,
    ...mapFeatureState,
  };
}

export default {
  namespaced: true,
  state() {
    return {
      brand_name: undefined,
      brand_logo: undefined,
      brand_favicon: undefined,
      default_web_locale: undefined,
      default_currency: undefined,
      recharge_amount_gradient: [],
      country_code: '',
      login_method: undefined,
      market_link: {},
      about_us: readCachedAboutUs(),
      privacy_policy: '#',
      terms_and_conditions: '#',
      web_lang_options: undefined,
      captcha: undefined,
      recaptcha: undefined,
      turnstile: undefined,
      device_code: undefined,
      auto_quick_login: false,
      is_guest: false,
      error_flag: false,
      app_mode: 0, // app模式:  0 - 普通模式, 1 - 直接弹宝, 2 - 跳转外部链接
      next_action: {}, // 外部链接url, app_mode值为2时有效
      venue_info: {},
      station: {},
      user_info_collection: [],
      internal_id: undefined,
      machine_precheck_error: undefined,
      map_enabled: false,
      map_enabled_by_host: false,
    }
  },

  mutations: {
    SET_APP_CONFIG(state, data) {
      Object.assign(state, data || {});
    },
    SET_DEVICE_CODE(state, deviceCode) {
      state.device_code = deviceCode
    },
    SET_ERROR_FLAG(state, flag) {
      state.error_flag = flag
    },
    SET_INTERNAL_ID(state, id) {
      state.internal_id = id
    },
    SET_MACHINE_PRECHECK_ERROR(state, error) {
      state.machine_precheck_error = error
    },
  },

  actions: {
    async fetchAppConfig({ state, commit, dispatch }) {
      const routePaths = [
        '/payment_select',
        '/payment_result',
        '/login',
        '/register_info',
        '/profile',
        '/gateway_redirect',
        '/moyasar_redirect',
        '/recharge_redirect',
        '/bind_redirect',
        '/renting_page',
        '/repayment_select',
        '/borrow_success',
        '/landing_page',
        '/error',
        '/code_verify',
        '/email_code_verify',
        '/pop_up_directly',
        '/machine_config_error',
        '/order_info',
        '/cabinet_map',
        '/help',
        '/report_problem',
        '/chat_with_us'
      ];

      commit('SET_INTERNAL_ID', undefined)

      const pathname = window.location.pathname;
      const isRouteDeviceCode = code => !!code && routePaths.includes(`/${code}`)
      if (pathname.includes('/payment_result')) {
        const params = new URLSearchParams(window.location.search || '');
        const internalId = params.get('internalId') || params.get('internal_id');
        commit('SET_INTERNAL_ID', internalId || undefined)
        return
      }

      const isRedirectPath =
        pathname.includes('/gateway_redirect') ||
        pathname.includes('/moyasar_redirect') ||
        pathname.includes('/recharge_redirect') ||
        pathname.includes('/bind_redirect') ||
        pathname.includes('/payment_redirect')
      if (isRedirectPath) {
        const lastDeviceCode = sessionStorage.getItem('lastDeviceCode')
        if (!state.device_code && lastDeviceCode) {
          commit('SET_DEVICE_CODE', lastDeviceCode)
        }
      } else {
        const lastSegment = pathname.split('/').filter(Boolean).pop();
        const isCodeInRoutes = routePaths.includes(`/${lastSegment}`);

        if (!isCodeInRoutes && lastSegment) {
          commit('SET_DEVICE_CODE', lastSegment);
          sessionStorage.setItem('lastDeviceCode', lastSegment);
        }
      }

      if (isRouteDeviceCode(state.device_code)) {
        commit('SET_DEVICE_CODE', undefined);
        sessionStorage.removeItem('lastDeviceCode');
      }

      try {
        commit('SET_ERROR_FLAG', false);
        commit('SET_MACHINE_PRECHECK_ERROR', undefined);
        const response = await fetch({
          url: '/webapp/config/v2',
          method: 'GET',
          params: state.device_code ? { code: state.device_code } : undefined
        });
        const appConfig = normalizeAppInitConfig(response.data);
        cacheAboutUs(appConfig.about_us);
        commit('SET_APP_CONFIG', appConfig);
        dispatch('setUserInfoCollection', appConfig.user_info_collection || [], { root: true })

        const quickLoginSupported = Array.isArray(appConfig.login_method)
          ? appConfig.login_method.some(method => method && method.name === 'quick_login')
          : false

        if (!quickLoginSupported && appConfig.is_guest && cks.get('token')) {
          await dispatch('setToken', '', { root: true })
          const base = (process.env.BASE_URL || '/').replace(/\/$/, '')
          window.location.replace(`${base}/login`)
          return
        }

        const precheckError = resolveMachinePrecheckError(appConfig.station, state.device_code);
        if (precheckError) {
          commit('SET_MACHINE_PRECHECK_ERROR', precheckError);
          return { machinePrecheckError: precheckError };
        }

        return { machinePrecheckError: undefined };
      } catch (error) {
        commit('SET_ERROR_FLAG', error.code || true);
        return { error };
      }
    }
  },

  getters: {
    brand_name: state => state.brand_name,
    brand_logo: state => state.brand_logo,
    brand_favicon: state => state.brand_favicon,
    country_code: state => state.country_code,
    default_web_locale: state => state.default_web_locale,
    login_method: state => state.login_method,
    market_link: state => state.market_link,
    about_us: state => state.about_us,
    ios_market_link: state => state.market_link && state.market_link.ios_market_link,
    android_market_link: state => state.market_link && state.market_link.android_market_link,
    privacy_policy: state => state.privacy_policy,
    terms_and_conditions: state => state.terms_and_conditions,
    web_lang_options: state => state.web_lang_options,
    captcha_enabled: state => state.captcha && state.captcha.enabled,
    captcha_provider: state => state.captcha && state.captcha.provider,
    captcha_site_key: state => state.captcha && state.captcha.site_key,
    recaptcha_open: state =>
      (state.captcha && state.captcha.enabled && state.captcha.provider === 'recaptcha') ||
      (state.recaptcha && state.recaptcha.recaptcha_open),
    recaptcha_site_key: state =>
      (state.captcha && state.captcha.enabled && state.captcha.provider === 'recaptcha' && state.captcha.site_key) ||
      (state.recaptcha && state.recaptcha.recaptcha_site_key),
    turnstile_open: state =>
      (state.captcha && state.captcha.enabled && state.captcha.provider === 'turnstile') ||
      (state.turnstile && state.turnstile.turnstile_open),
    turnstile_site_key: state =>
      (state.captcha && state.captcha.enabled && state.captcha.provider === 'turnstile' && state.captcha.site_key) ||
      (state.turnstile && state.turnstile.turnstile_site_key),
    device_code: state => state.device_code,
    auto_quick_login: state => state.auto_quick_login,
    is_guest: state => state.is_guest,
    error_flag: state => state.error_flag,
    app_mode: state => state.app_mode,
    next_action: state => state.next_action,
    redirect_url: state => state.next_action.redirect_url,
    outlet_id: state => state.station.outlets_id || state.venue_info.id || 0,
    default_currency: state => state.default_currency,
    recharge_amount_gradient: state => state.recharge_amount_gradient,
    internal_id: state => state.internal_id,
    machine_precheck_error: state => state.machine_precheck_error,
    map_enabled: state => state.map_enabled,
    map_enabled_by_host: state => state.map_enabled_by_host,
  }
}
